import { useCallback, useState } from 'react';

import { getElevationColor } from '@/endpoints';
import getElevationGeoJson from '@/endpoints/schoolcraft/elevation/getElevationGeojson';
import { IElevationColorResponse } from '@/models/elevation';
import { IElevationGeojsonResponse } from '@/redux/api/geoserverApi';
import Logger from '@/utils/logger';

export interface IElevationData {
  color: IElevationColorResponse | null;
  elevation: IElevationGeojsonResponse | null;
}

export function useEmbedElevation(boundary) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);
  const [geojsonLoading, setGeojsonLoading] = useState<boolean>(false);
  const [elevationData, setElevationData] = useState<IElevationData>({
    color: null,
    elevation: null,
  });

  const fetchElevationColor = useCallback(async () => {
    if (!boundary) {
      return;
    }

    try {
      setLoading(true);
      const { res, err } = await getElevationColor({
        boundary,
        units: 'feet',
      });

      if (err) {
        setError(err);
        throw Error(err.message);
      }

      if (!res) {
        throw Error('No data');
      }

      setElevationData((prev) => {
        return {
          ...prev,
          color: res.data,
        };
      });
    } catch (err) {
      Logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [boundary]);

  const fetchElevationGeoJson = useCallback(async () => {
    if (!boundary) {
      return;
    }

    try {
      setGeojsonLoading(true);
      const { res, err } = await getElevationGeoJson({
        boundary,
        line_sep: 10,
        units: 'feet',
      });

      if (err) {
        throw Error(err.message);
      }

      if (!res) {
        throw Error('No data');
      }

      setElevationData((prev) => {
        return {
          ...prev,
          elevation: res.data,
        };
      });
    } catch (err) {
      Logger.error(err);
    } finally {
      setGeojsonLoading(false);
    }
  }, [boundary]);

  const fetchElevation = useCallback(() => {
    fetchElevationGeoJson();
    fetchElevationColor();
  }, [fetchElevationColor, fetchElevationGeoJson]);

  return {
    elevationData,
    error,
    fetchData: fetchElevation,
    geojsonLoading,
    loading,
  };
}
